<script setup lang="ts">
/**
 * Ikony działów katalogu.
 *
 * Klientka poprosiła wprost (16.09.2026), żeby kategorie nie miały zdjęć,
 * tylko ikony w firmowym turkusie — wzorem monstera.pl, gdzie pasek działów
 * jest rysunkowy, a nie fotograficzny. Zdjęcie aranżacyjne pokazuje JEDEN
 * produkt z działu i przez to zawęża, ikona mówi „to cała półka".
 *
 * Inline SVG, nie pliki: czternaście ikon to czternaście żądań, a tak kolor
 * bierze się z `currentColor` i idzie za motywem marki — ten sam wzorzec co
 * `TechnikaIkona`. Wzorzec trzyma je jako PNG-i 96×90, czyli rastry, które
 * przy zmianie koloru marki trzeba by przerysować.
 *
 * Klucz to SLUG działu, nie id — identyfikatory zmienią się przy replikacji
 * z EasyItem, slug jest stabilny. Dział bez rysunku dostaje kształt zapasowy,
 * więc nowa kategoria nie robi dziury w pasku.
 *
 * ZESTAW (16.10.2026, Asana 1219566675747705). Pierwsza wersja rysowała
 * działy z planu katalogu sprzed replikacji (`biuro-i-pismiennicze`,
 * `podroz-i-torby`, `upominki`…) i trafiała w sześć z czternastu działów
 * z menu — reszta dostawała tę samą siatkę kwadratów. Klucze są teraz 1:1
 * ze slugami z `/api/store/nav`.
 *
 * Jeden język rysunku, jak grafiki marki: siatka 24×24, linia 1,5 z okrągłymi
 * końcami, miękkie łuki (pałąk słuchawek, czasza parasola, słońce, łuk drzwi).
 * Każdy rysunek ma kontur i JEDEN detal drugiego planu (`class="akcent"`) —
 * pasek na tytułowej maluje go innym kolorem marki (`--kategoria-ikona-akcent`),
 * a bez tej zmiennej detal jest przygaszonym konturem, jak dotąd.
 */
const props = defineProps<{
  /** Slug działu; nieznany rysuje kształt zapasowy. */
  nazwa: string
}>()

const RYSUNKI: Record<string, string> = {
  // Kubek termiczny z pokrywką i opaską.
  'do-picia': `
    <path d="M7.6 4.2h8.8a1.1 1.1 0 0 1 1.1 1.1v1.9h-11V5.3a1.1 1.1 0 0 1 1.1-1.1z" />
    <path d="M7.1 7.2l1.2 12.3a1.7 1.7 0 0 0 1.7 1.5h4a1.7 1.7 0 0 0 1.7-1.5l1.2-12.3" />
    <path class="akcent" d="M7.8 12.4h8.4" />
    <path class="akcent" d="M13.3 5.7h1.5" />`,

  // Plecak: korpus, ucho i kieszeń.
  'torby-i-plecaki': `
    <path d="M6 11a6 6 0 0 1 12 0v8.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 19.5z" />
    <path d="M10 5.3v-.7a2 2 0 0 1 4 0v.7" />
    <path class="akcent" d="M9 15h6v2.8a1.2 1.2 0 0 1-1.2 1.2h-3.6A1.2 1.2 0 0 1 9 17.8z" />
    <path class="akcent" d="M9.5 12.2h5" />`,

  // Słuchawki — czytelniejsze w 24 px niż power bank, a dział obejmuje
  // i audio, i ładowarki.
  elektronika: `
    <path d="M4.5 14.5V12a7.5 7.5 0 0 1 15 0v2.5" />
    <rect x="3.5" y="13.5" width="4" height="7" rx="1.8" />
    <rect x="16.5" y="13.5" width="4" height="7" rx="1.8" />
    <path class="akcent" d="M7.4 12a4.6 4.6 0 0 1 9.2 0" />`,

  // Długopis pod kątem i linia pisma.
  'do-pisania': `
    <path d="M4.5 19.5l.8-3.5 9.9-9.9a1.9 1.9 0 0 1 2.7 2.7L8 18.7z" />
    <path class="akcent" d="M13.6 7.7l2.7 2.7" />
    <path class="akcent" d="M12.5 20.5c1.2-.9 2.4-.9 3.6 0s2.4.9 3.6 0" />`,

  // Notes na spirali — dział to głównie notatniki, teczki i akcesoria biurowe.
  biuro: `
    <rect x="6" y="3.5" width="13" height="17" rx="1.8" />
    <path d="M4.2 7.5h3.6M4.2 12h3.6M4.2 16.5h3.6" />
    <path class="akcent" d="M10.5 8.5h5M10.5 12h5M10.5 15.5h3" />`,

  // Walizka z pasami.
  podroz: `
    <rect x="4.5" y="7" width="15" height="12.5" rx="2.2" />
    <path d="M9.3 7V5.4a1.2 1.2 0 0 1 1.2-1.2h3a1.2 1.2 0 0 1 1.2 1.2V7" />
    <path class="akcent" d="M8.5 7v12.5M15.5 7v12.5" />`,

  // Parasol: czasza z falbaną i rączka.
  'parasole-i-peleryny': `
    <path d="M3 12a9 9 0 0 1 18 0c-1.5-1.1-3-1.1-4.5 0-1.5-1.1-3-1.1-4.5 0-1.5-1.1-3-1.1-4.5 0-1.5-1.1-3-1.1-4.5 0z" />
    <path d="M12 12v6.8a2 2 0 0 1-4 0" />
    <path class="akcent" d="M12 3c-2.6 2.6-4.1 5.6-4.5 9M12 3c2.6 2.6 4.1 5.6 4.5 9" />`,

  // Kółko na klucze z zawieszką.
  breloki: `
    <circle cx="7.2" cy="7.2" r="3.7" />
    <path d="M9.8 9.8l1.7 1.7" />
    <rect x="10.6" y="10.6" width="10" height="10" rx="3" />
    <circle class="akcent" cx="14.2" cy="14.2" r="1.1" />`,

  // Słońce nad falą — plaża, piknik, sport na powietrzu.
  wypoczynek: `
    <path d="M7 14.2a5 5 0 0 1 10 0" />
    <path d="M3.5 14.2h17" />
    <path d="M12 7.9V5.7M7.5 9.7L6 8.2M16.5 9.7L18 8.2" />
    <path class="akcent" d="M4 17.7c1.3-.9 2.7-.9 4 0s2.7.9 4 0 2.7-.9 4 0 2.7.9 4 0" />`,

  // Puzzel — gry, zabawki i przybory szkolne.
  'rozrywka-i-szkoa': `
    <path d="M4.6 8.5h3.7a2.3 2.3 0 1 1 3.6 0h3.7V12a2.3 2.3 0 1 1 0 3.6v3.9h-11z" />
    <path class="akcent" d="M7.6 11.6a2.4 2.4 0 0 1 2.4-2.4" />`,

  // Domek z łukiem drzwi.
  dom: `
    <path d="M3.5 10.7L12 3.7l8.5 7" />
    <path d="M5.9 8.9v10.2a1.4 1.4 0 0 0 1.4 1.4h9.4a1.4 1.4 0 0 0 1.4-1.4V8.9" />
    <path class="akcent" d="M10 20.5v-4.2a2 2 0 0 1 4 0v4.2" />`,

  // Klucz płaski — główka z wycięciem i trzonek z rowkiem.
  narzedzia: `
    <path d="M18.4 3.76A4.6 4.6 0 1 0 12.06 9.82l-6.62 6.62a1.5 1.5 0 0 0 2.12 2.12l6.63-6.62A4.6 4.6 0 0 0 20.24 5.6l-2.48 2.48-1.84-1.83z" />
    <path class="akcent" d="M8.6 15.4l2.8-2.8" />`,

  // Koszulka z polem nadruku — dział to odzież pod znakowanie.
  tekstylia: `
    <path d="M9 3.5L4.5 5.6l1.7 4.1 2.1-.9v11a1.2 1.2 0 0 0 1.2 1.2h5a1.2 1.2 0 0 0 1.2-1.2v-11l2.1.9 1.7-4.1L15 3.5a3 3 0 0 1-6 0z" />
    <rect class="akcent" x="10.4" y="10.6" width="3.2" height="2.6" rx=".6" />`,

  // Pudełko z pokrywą, wstążką i kokardą.
  'zestawy-upominkowe': `
    <rect x="3.5" y="8" width="17" height="3.6" rx="1.1" />
    <path d="M5 11.6V19a1.4 1.4 0 0 0 1.4 1.4h11.2A1.4 1.4 0 0 0 19 19v-7.4" />
    <path class="akcent" d="M12 8v12.4" />
    <path d="M12 8c-1-2.6-3.4-4.3-4.6-3.1-1 1 .4 3.1 4.6 3.1zM12 8c1-2.6 3.4-4.3 4.6-3.1 1 1-.4 3.1-4.6 3.1z" />`,

  // Nie dział, tylko strona /nowosci — jej baner ma wyglądać jak banery działów
  // (klientka 03.11.2026). Duża czteroramienna iskra i dwie małe: „świeże”.
  nowosci: `
    <path d="M10 3.5c.5 3.9 2.6 6 6.5 6.5-3.9.5-6 2.6-6.5 6.5-.5-3.9-2.6-6-6.5-6.5 3.9-.5 6-2.6 6.5-6.5z" />
    <path class="akcent" d="M17.5 14v6M14.5 17h6" />
    <path class="akcent" d="M18 3.5v3M16.5 5h3" />`,

  // Kształt zapasowy: dział, dla którego nie ma jeszcze rysunku. Neutralna
  // siatka zamiast pustego miejsca — pasek nie może mieć dziur.
  zapasowa: `
    <rect x="3.6" y="3.6" width="7.4" height="7.4" rx="1.2" />
    <rect class="akcent" x="13" y="3.6" width="7.4" height="7.4" rx="1.2" />
    <rect class="akcent" x="3.6" y="13" width="7.4" height="7.4" rx="1.2" />
    <rect class="akcent" x="13" y="13" width="7.4" height="7.4" rx="1.2" />`,
}

/**
 * Slugi, które wskazują istniejący rysunek. „rozrywka-i-szkoa" to slug bez
 * „ł" — błąd transliteracji po stronie EasyItem. Gdy go naprawią, dział
 * dostanie poprawny slug i nie może wtedy spaść na kształt zapasowy.
 */
const ALIASY: Record<string, string> = {
  'rozrywka-i-szkola': 'rozrywka-i-szkoa',
}

const rysunek = computed(
  () => RYSUNKI[props.nazwa] ?? RYSUNKI[ALIASY[props.nazwa] ?? ''] ?? RYSUNKI.zapasowa,
)
</script>

<template>
  <svg
    class="kategoria-ikona"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <!-- Rysunek pochodzi wyłącznie ze stałej w tym pliku — nie z API, nie
         z treści użytkownika — więc v-html nie wystawia tu powierzchni XSS.
         Dyrektywa musi być w JEDNEJ linii, inaczej ESLint jej nie zobaczy
         (tak jest w TechnikaIkona.vue, stąd ostrzeżenie w tamtym pliku). -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <g v-html="rysunek" />
  </svg>
</template>

<style scoped>
.kategoria-ikona {
  display: block;
  width: 100%;
  height: 100%;
  color: inherit;
}

/* Detal drugiego planu. Treść z v-html nie dostaje atrybutu zakresu, stąd
   :deep. Bez zmiennych (np. baner działu) zostaje przygaszonym konturem. */
.kategoria-ikona :deep(.akcent) {
  stroke: var(--kategoria-ikona-akcent, currentColor);
  opacity: var(--kategoria-ikona-akcent-krycie, 0.55);
}
</style>
